import { useState } from 'react';
import { Button, Typography, useTranslate } from '@prenuvo/halo';

import { useUser } from '@/core/user';
import { useStudyGuard } from '@/shared/guard';
import { AutoHeightTextArea, Required } from '@/shared/ui';

import { useCreateActivityLog } from './use-create-activity-log.hook';
import { useCreateBreakTheGlassNote } from './use-create-break-the-glass-note.hook';
import { useCreateStudyLog } from './use-create-study-log.hook';

const SEPARATOR = ' - ';
const NAME_SEPARATOR = '\n- ';

export type PatientContactOutcomeSectionProps = {
  closeDialog: () => void;
  reason: string;
};

export function PatientContactOutcomeSection({
  closeDialog,
  reason,
}: PatientContactOutcomeSectionProps) {
  const [callDescription, setCallDescription] = useState<string>('');

  const { patient } = useStudyGuard();
  const { user } = useUser();
  const { createActivityLog, isCreatingActivityLog } = useCreateActivityLog();
  const { isCreatingBreakTheGlassNote, onCreateBreakTheGlassNote } = useCreateBreakTheGlassNote();
  const { createStudyLog, isCreatingStudyLog } = useCreateStudyLog();
  const translate = useTranslate();

  const reasonText = translate(reason) ?? '';

  const isSubmitting = isCreatingBreakTheGlassNote || isCreatingActivityLog || isCreatingStudyLog;

  const handleSubmit = () => {
    onCreateBreakTheGlassNote(
      `${reasonText}${SEPARATOR}${callDescription}${NAME_SEPARATOR}${user.fullname}`,
    );

    createActivityLog(
      {
        fullObject: {
          descriptionOfCall: callDescription,
        },
      },
      () => {
        createStudyLog(
          {
            body: `FCT access to Patient Info - Reason: ${reasonText}, Description of call: ${callDescription}`,
          },
          closeDialog,
        );
      },
    );
  };

  return (
    <div>
      <Typography
        className="text-neutral-400 dark:text-neutral-400"
        translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.LEGAL_NAME"
        variant="paragraph-sm"
      />

      <Typography
        as="p"
        className="mb-4 text-neutral-50"
        translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.PATIENT_NAME"
        values={{ name: `${patient.lastname}, ${patient.firstname}` }}
      />

      <Typography
        className="text-neutral-400 dark:text-neutral-400"
        translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.PHONE_NUMBER"
        variant="paragraph-sm"
      />

      <Typography
        as="p"
        className="mb-4 text-neutral-50"
        translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.PATIENT_PHONE"
        values={{ phone: patient.phone_number }}
      />

      <Required className={{ root: 'mb-1 flex' }} isRequired>
        <Typography
          as="label"
          className="text-neutral-400 dark:text-neutral-400"
          translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.PATIENT_OUTCOME_INPUT_TITLE"
          variant="paragraph-sm"
        />
      </Required>

      <AutoHeightTextArea
        aria-label={translate('FCT.REPORTING.HEADER.CONTACT_DETAILS.PATIENT_OUTCOME_INPUT_TITLE')}
        className="p-2 px-3"
        onChange={(e) => setCallDescription(e.target.value)}
        value={callDescription}
        variant="outlined"
      />

      <div className="mt-4 mb-2 flex justify-end">
        <Button
          className={{ root: 'px-8' }}
          data-testid="submit-button"
          disabled={!callDescription.trim() || isSubmitting}
          onClick={handleSubmit}
          translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.SUBMIT"
          variant="primary"
        />
      </div>
    </div>
  );
}
